import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { PlayCircle, RefreshCw, Calendar, CheckCircle2, AlertTriangle, Clock } from "lucide-react";

export default function AdminScrapers() {
  const [data, setData] = useState({ sources: [], runs: [] });
  const [running, setRunning] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/scrapers/runs");
      setData(r.data);
    } catch {
      toast.error("Failed to load import history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runNow = async (source) => {
    setRunning(source);
    try {
      const r = await api.post(`/admin/scrapers/${source}/run`);
      const d = r.data;
      if (d.error) {
        toast.error(`Import failed: ${d.error}`);
      } else if (d.skipped) {
        toast(`Skipped — ${d.reason}`);
      } else {
        toast.success(`${d.events_new} new · ${d.events_skipped} already imported (of ${d.events_found} found)`);
      }
      await load();
    } catch {
      toast.error("Trigger failed");
    } finally {
      setRunning(null);
    }
  };

  const lastRunFor = (sourceId) => data.runs.find((r) => r.source === sourceId);

  return (
    <div className="space-y-6" data-testid="admin-scrapers">
      <div className="bg-white rounded-2xl border border-black/5 p-6 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-heading text-xl text-navy">Event auto-imports</h3>
            <p className="text-sm text-neutral-500 mt-1">
              Scraped events land as <span className="font-medium text-navy">Pending</span> — approve them from the Submissions tab.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} className="rounded-full" data-testid="scrapers-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {data.sources.map((s) => {
            const last = lastRunFor(s.id);
            const isRunning = running === s.id;
            return (
              <div key={s.id} className="rounded-xl border border-black/5 bg-sand/10 p-5" data-testid={`scraper-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-heading text-navy text-lg">{s.label}</div>
                    <div className="text-xs text-neutral-500 mt-0.5 flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> Auto: {s.schedule}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    disabled={isRunning}
                    onClick={() => runNow(s.id)}
                    className="rounded-full bg-navy hover:bg-navy/90 shrink-0"
                    data-testid={`scraper-run-${s.id}`}
                  >
                    <PlayCircle className="w-3.5 h-3.5 mr-1" />
                    {isRunning ? "Running…" : "Run now"}
                  </Button>
                </div>
                {last ? (
                  <div className="mt-4 pt-3 border-t border-black/5 text-sm">
                    <div className="flex items-center gap-2">
                      {last.status === "ok" ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                      )}
                      <span className="text-navy font-medium">
                        {last.status === "ok"
                          ? `${last.events_new} new · ${last.events_skipped} already had · ${last.events_found} found`
                          : `Error — ${last.error || "unknown"}`}
                      </span>
                    </div>
                    <div className="text-xs text-neutral-500 mt-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(last.started_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                ) : (
                  <p className="mt-4 pt-3 border-t border-black/5 text-xs text-neutral-500 italic">
                    No runs yet — click &quot;Run now&quot; to import for the first time.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {data.runs.length > 0 && (
        <div className="bg-white rounded-2xl border border-black/5 p-6">
          <h3 className="font-heading text-lg text-navy mb-4">Recent runs</h3>
          <div className="space-y-2">
            {data.runs.slice(0, 15).map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-4 text-sm py-2 border-b border-black/5 last:border-0" data-testid={`run-${r.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {r.status === "ok" ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                  )}
                  <span className="font-medium text-navy truncate">
                    {data.sources.find((s) => s.id === r.source)?.label || r.source}
                  </span>
                </div>
                <div className="text-neutral-500 shrink-0 text-xs">
                  {r.status === "ok"
                    ? `+${r.events_new} new · ${r.events_found} found`
                    : (r.error || "error").slice(0, 60)}
                </div>
                <div className="text-neutral-400 shrink-0 text-xs w-32 text-right">
                  {new Date(r.started_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
